import logger from '@ayocore/platform/common/helpers/logger';
import type { CommentMention } from '@ayocore/comments/common/types/comment-mention';
import type { CommentContactMention } from '@ayocore/comments/common/types/comment-contact-mention';
import type { AwaitableRpcClient } from '@ayocore/platform/server/rpc/types';
import type { CommentRepository } from '@ayocore/comments/server/repositories/CommentRepository';
import type { TaskRepository } from '@ayocore/tasks/server/repositories/TaskRepository';
import type { TaskStatusRepository } from '@ayocore/tasks/server/repositories/TaskStatusRepository';
import type { ActivityCommentScope } from '@ayocore/activity/common/types/activity-comment-scope';
import type { ActivityChannelMessage } from '@ayocore/activity/common/types/activity-message';
import type { ActivityEventRepository } from '@ayocore/activity/server/repositories/ActivityEventRepository';
import type { ActivityPinRepository } from '@ayocore/activity/server/repositories/ActivityPinRepository';
import type { ActivityThreadRepository } from '@ayocore/activity/server/repositories/ActivityThreadRepository';
import type { ReconcileSourcesResult } from '@ayocore/activity/server/activity/reconcile-from-sources';
import type { ActivityThreadQuickFilter } from '@ayocore/activity/common/types/activity-thread-quick-filter';
import type { ActivityThread } from '@ayocore/activity/schemas/activity-thread.schema';
import { feedItemFromThread } from '../utils/activity-thread-to-feed-item';
import { ControllerStore } from './ControllerStore';
import { ActivityChatController } from './ActivityChatController';
import { ActivityComposerController } from './ActivityComposerController';
import { ActivityPinsController } from './ActivityPinsController';
import { ActivityThreadsController } from './ActivityThreadsController';
import { INITIAL_ROOT_STATE, type ActivityRootState } from './types';

type RootRepositories = {
  threadRepository: AwaitableRpcClient<ActivityThreadRepository>;
  eventRepository: AwaitableRpcClient<ActivityEventRepository>;
  pinRepository: AwaitableRpcClient<ActivityPinRepository>;
  commentRepository: AwaitableRpcClient<CommentRepository>;
  taskRepository: AwaitableRpcClient<TaskRepository>;
  taskStatusRepository: AwaitableRpcClient<TaskStatusRepository>;
};

/**
 * ActivityController — root-оркестратор Activity.
 */
export class ActivityController extends ControllerStore<ActivityRootState> {
  readonly threads: ActivityThreadsController;

  readonly chat: ActivityChatController;

  readonly pins: ActivityPinsController;

  readonly composer: ActivityComposerController;

  private unsubscribeThreads: (() => void) | null = null;

  constructor(repositories: RootRepositories) {
    super(INITIAL_ROOT_STATE);

    this.threads = new ActivityThreadsController(repositories.threadRepository);
    this.chat = new ActivityChatController({
      eventRepository: repositories.eventRepository,
      commentRepository: repositories.commentRepository,
      taskRepository: repositories.taskRepository,
      taskStatusRepository: repositories.taskStatusRepository,
    });
    this.pins = new ActivityPinsController(repositories.pinRepository);
    this.composer = new ActivityComposerController();

    this.unsubscribeThreads = this.threads.subscribe(() => {
      this.syncSelectedThreadFromThreads();
    });
  }

  /**
   * Обновляет rpc-зависимости root и дочерних контроллеров.
   */
  updateRepositories(repositories: Partial<RootRepositories>): void {
    if (repositories.threadRepository) {
      this.threads.updateThreadRepository(repositories.threadRepository);
    }
    if (repositories.pinRepository) {
      this.pins.updatePinRepository(repositories.pinRepository);
    }
    this.chat.updateRepositories({
      ...(repositories.eventRepository ? { eventRepository: repositories.eventRepository } : {}),
      ...(repositories.commentRepository
        ? { commentRepository: repositories.commentRepository }
        : {}),
      ...(repositories.taskRepository ? { taskRepository: repositories.taskRepository } : {}),
      ...(repositories.taskStatusRepository
        ? { taskStatusRepository: repositories.taskStatusRepository }
        : {}),
    });
  }

  /**
   * Загружает список тредов и синхронизирует selectedThread.
   */
  async loadThreads(options?: {
    showLoader?: boolean;
    quickFilter?: ActivityThreadQuickFilter;
    isArchiveView?: boolean;
  }): Promise<void> {
    await this.threads.load(options);
    this.syncSelectedThreadFromThreads();
  }

  /**
   * Переключает quick filter.
   */
  async setQuickFilter(quickFilter: ActivityThreadQuickFilter): Promise<void> {
    await this.threads.setQuickFilter(quickFilter);
    this.syncSelectedThreadFromThreads();
  }

  /**
   * Переключает режим архива.
   */
  async setArchiveView(isArchiveView: boolean): Promise<void> {
    await this.threads.setArchiveView(isArchiveView);
    this.syncSelectedThreadFromThreads();
  }

  /**
   * Обновляет флаг избранного треда.
   */
  async setThreadFavorite(threadId: number, isFavorite: boolean): Promise<void> {
    await this.threads.setThreadFavorite(threadId, isFavorite);
    this.syncSelectedThreadFromThreads();
  }

  /**
   * Обновляет флаг закрепления треда.
   */
  async setThreadPinned(threadId: number, isPinned: boolean): Promise<void> {
    await this.threads.setThreadPinned(threadId, isPinned);
    this.syncSelectedThreadFromThreads();
  }

  /**
   * Архивирует/разархивирует тред.
   */
  async setThreadArchived(threadId: number, isArchived: boolean): Promise<void> {
    await this.threads.setThreadArchived(threadId, isArchived);
    this.syncSelectedThreadFromThreads();
  }

  /**
   * Помечает тред прочитанным через ThreadsController.
   */
  async markThreadRead(threadId: number): Promise<void> {
    await this.threads.markThreadRead(threadId);
    this.syncSelectedThreadFromThreads();
  }

  /**
   * Выбор треда и сброс transient UI-state дочерних контроллеров.
   *
   * `threadSnapshot` — опциональный базовый ActivityThread, используется в embed-сценарии
   * (карточка лида/партнёра), когда тред получен через `findByEntity` ДО того как
   * `loadThreads` принёс полноценный FeedItem. Если items пуст и snapshot передан —
   * заполним extras дефолтами и сохраним. После `loadThreads` `syncSelectedThreadFromThreads`
   * заменит этот объект на настоящий FeedItem (если найдётся).
   */
  selectThread(threadId: number | null, threadSnapshot?: ActivityThread): void {
    const snapshot = this.getSnapshot();
    if (snapshot.selectedThreadId === threadId) return;

    logger.debug('[Activity:Root] selectThread', { threadId });
    this.batch(() => {
      this.chat.invalidatePending();
      this.pins.invalidatePending();

      this.chat.clearThreadData();
      this.pins.clearPins();
      this.composer.reset();

      const fromItems = this.threads.findThreadById(threadId);
      const selectedThread =
        fromItems ?? (threadSnapshot ? feedItemFromThread(threadSnapshot) : null);
      this.setState({
        selectedThreadId: threadId,
        selectedThread,
      });
      this.chat.setActiveThreadId(threadId);
    });

    if (!threadId) return;

    void this.chat.loadChat(threadId);
    void this.pins.loadPins(threadId);
  }

  /**
   * Отправляет комментарий в выбранный тред и сбрасывает transient composer/reply state при успехе.
   */
  async sendComment(
    content: string,
    mentionedUserIds?: number[],
    mentionedUserMentions?: CommentMention[],
    composerScope?: ActivityCommentScope | null,
    mentionedContactUuids?: string[],
    mentionedContactMentions?: CommentContactMention[],
    fileIds?: number[]
  ): Promise<boolean> {
    const selectedThread = this.getSnapshot().selectedThread;
    if (!selectedThread) return false;

    const sent = await this.chat.sendComment({
      entityType: selectedThread.entity_type,
      entityUuid: selectedThread.entity_uuid,
      content,
      threadId: selectedThread.id,
      mentionedUserIds,
      mentionedUserMentions,
      mentionedContactUuids,
      mentionedContactMentions,
      composerScope: composerScope ?? null,
      fileIds,
    });
    if (sent) {
      this.composer.reset();
      this.chat.cancelReply();
    }
    return sent;
  }

  /**
   * Обрабатывает realtime-сообщение и планирует перезагрузку затронутых частей.
   */
  handleRealtimeMessage(message: ActivityChannelMessage): void {
    if (!message.thread_id) return;
    logger.debug('[Activity:Root] realtime message', {
      threadId: message.thread_id,
      eventId: message.event_id,
    });

    this.threads.scheduleReload();

    const selectedThreadId = this.getSnapshot().selectedThreadId;
    if (!selectedThreadId || selectedThreadId !== message.thread_id) return;

    this.chat.scheduleChatReload(selectedThreadId);
    this.pins.schedulePinsReload(selectedThreadId);
  }

  /**
   * Реконсиляция выбранного треда из первоисточников и перезагрузка
   * чата/пинов/ленты. Возвращает счётчики восстановленного (для тоста в UI),
   * null — если тред не выбран.
   */
  async reconcileSelectedThread(): Promise<ReconcileSourcesResult | null> {
    const threadId = this.getSnapshot().selectedThreadId;
    if (!threadId) return null;

    const result = await this.threads.reconcileThread(threadId);

    void this.chat.loadChat(threadId, { showLoader: false });
    void this.pins.loadPins(threadId, { showLoader: false });
    void this.threads.load({ showLoader: false });

    return result;
  }

  /**
   * Принудительно синхронизирует threads + выбранный chat/pins после reconnection.
   */
  forceResync(): void {
    void this.threads.load({ showLoader: false });
    const threadId = this.getSnapshot().selectedThreadId;
    if (!threadId) return;
    void this.chat.loadChat(threadId, { showLoader: false });
    void this.pins.loadPins(threadId, { showLoader: false });
  }

  /**
   * Каскадно освобождает ресурсы root и дочерних контроллеров.
   */
  dispose(): void {
    if (this.unsubscribeThreads) {
      this.unsubscribeThreads();
      this.unsubscribeThreads = null;
    }
    this.threads.dispose();
    this.chat.dispose();
    this.pins.dispose();
    this.composer.dispose();
    super.dispose();
  }

  private syncSelectedThreadFromThreads(): void {
    const snapshot = this.getSnapshot();
    const selectedThreadId = snapshot.selectedThreadId;
    if (!selectedThreadId) {
      if (snapshot.selectedThread !== null) {
        this.setState({ selectedThread: null });
      }
      return;
    }

    const selectedFromItems = this.threads.findThreadById(selectedThreadId);
    if (selectedFromItems) {
      if (snapshot.selectedThread !== selectedFromItems) {
        this.setState({ selectedThread: selectedFromItems });
      }
      return;
    }

    const threadsState = this.threads.getSnapshot();
    const shouldKeepSelectedOutsideFilteredList =
      threadsState.quickFilter !== 'all' && snapshot.selectedThread?.id === selectedThreadId;
    if (shouldKeepSelectedOutsideFilteredList) return;

    // Если selectedThread установлен явно (embed-сценарий через selectThread(id, snapshot))
    // и его id совпадает с selectedThreadId — оставляем как есть. Следующий tick sync
    // обновит на полный FeedItem, если тред появится в items.
    if (snapshot.selectedThread && snapshot.selectedThread.id === selectedThreadId) {
      return;
    }

    this.batch(() => {
      this.chat.setActiveThreadId(null);
      this.chat.clearThreadData();
      this.pins.clearPins();
      this.composer.reset();
      this.setState({
        selectedThreadId: null,
        selectedThread: null,
      });
    });
  }
}
